import { useEffect, useState, useCallback } from "react";
import { Loader2, Truck, Phone, MapPin, Bike, PartyPopper } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa, formatDate, statusColor, DELIVERY_LABELS, PAYMENT_METHOD_LABELS } from "../../lib/format";
import { toast } from "sonner";

export default function Deliveries() {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(null);
  const [livreModal, setLivreModal] = useState(null);
  const [livreurName, setLivreurName] = useState("");
  const [livreurFeeProposed, setLivreurFeeProposed] = useState("");
  const [livreurFee, setLivreurFee] = useState("");
  const [livreurPaid, setLivreurPaid] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/orders").then(({ data }) => setOrders(data.filter((o) => o.order_status !== "annulee")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDelivery = async (order, status) => {
    setBusy(order.id);
    try {
      await api.patch(`/admin/orders/${order.id}/delivery`, { delivery_status: status });
      toast.success("Livraison mise à jour");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const waPhone = (p) => {
    const d = (p || "").replace(/\D/g, "");
    if (!d) return "";
    if (d.startsWith("228")) return d;
    if (d.length <= 8) return "228" + d;
    return d;
  };

  const notifyEnRoute = (order) => {
    const phone = waPhone(order.customer_phone);
    if (!phone) { toast.error("Numéro du client manquant"); return; }
    const msg = `Bonjour ${order.customer_name} ! Votre commande NOUMABOU ${order.order_number} est en route. Le livreur arrive bientôt, merci de vous tenir prêt(e). Total à régler : ${fcfa(order.total)}.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success("WhatsApp ouvert — appuyez sur Envoyer 🛵");
    setDelivery(order, "en_cours");
  };

  const openLivre = (order) => {
    setLivreModal(order);
    setLivreurName("");
    setLivreurFeeProposed("");
    setLivreurFee(order.delivery_fee ? String(order.delivery_fee) : "");
    setLivreurPaid(false);
  };

  const confirmLivree = async () => {
    const order = livreModal;
    setBusy(order.id);
    try {
      await api.patch(`/admin/orders/${order.id}/delivery`, {
        delivery_status: "livre",
        livreur_name: livreurName.trim(),
        livreur_fee: Number(livreurFee) || 0,
        livreur_fee_proposed: Number(livreurFeeProposed) || 0,
        livreur_paid: livreurPaid,
      });
      const phone = waPhone(order.customer_phone);
      const shopUrl = window.location.origin + "/boutique";
      const msg = `Merci ${order.customer_name} ! Votre commande NOUMABOU ${order.order_number} a bien été livrée. Nous espérons que vous en êtes satisfait(e) 🙏. Laissez-nous un avis ou recommandez-nous : ${shopUrl}`;
      if (phone) window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
      toast.success("Livrée ✅ — WhatsApp de remerciement ouvert 🙏");
      setLivreModal(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (orders === null) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  const active = orders.filter((o) => o.delivery_status !== "livre");
  const done = orders.filter((o) => o.delivery_status === "livre");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Livraisons</h1>
        <p className="text-brand-muted mt-1">{active.length} livraison(s) en cours</p>
      </div>

      <div className="space-y-3">
        {active.length === 0 && <p className="text-brand-muted bg-white border border-brand-line rounded-xl p-8 text-center">Aucune livraison en attente 🎉</p>}
        {active.map((o) => (
          <div key={o.id} data-testid={`delivery-card-${o.id}`} className="bg-white border border-brand-line rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-heading font-semibold text-brand-ink">{o.order_number}</p>
                <p className="text-sm text-brand-sub flex items-center gap-1 mt-1"><Phone className="w-3.5 h-3.5" /> {o.customer_name} · {o.customer_phone}</p>
                {o.customer_address && <p className="text-sm text-brand-muted flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {o.customer_address}</p>}
              </div>
              <div className="text-right">
                <p className="font-heading font-black text-brand-ink">{fcfa(o.total)}</p>
                <p className="text-xs text-brand-muted">{PAYMENT_METHOD_LABELS[o.payment_method]}</p>
              </div>
            </div>
            <div className="mt-3 text-sm text-brand-sub">
              {o.items.map((it, i) => <span key={i}>{it.qty}× {it.name}{i < o.items.length - 1 ? ", " : ""}</span>)}
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusColor(o.delivery_status)}`}>{DELIVERY_LABELS[o.delivery_status]}</span>
              <div className="flex-1" />
              {o.delivery_status !== "en_cours" && o.delivery_status !== "livre" && (
                <button disabled={busy === o.id} data-testid={`notify-en-route-${o.id}`} onClick={() => notifyEnRoute(o)}
                  className="text-xs px-3 py-2 rounded-full font-semibold bg-green-600 text-white hover:bg-green-700 flex items-center gap-1.5 disabled:opacity-60">
                  <Bike className="w-3.5 h-3.5" /> Livreur en route (prévenir le client)
                </button>
              )}
              {o.delivery_status !== "livre" && (
                <button disabled={busy === o.id} data-testid={`notify-livree-${o.id}`} onClick={() => openLivre(o)}
                  className="text-xs px-3 py-2 rounded-full font-semibold bg-brand text-white hover:bg-brand-hover flex items-center gap-1.5 disabled:opacity-60">
                  <PartyPopper className="w-3.5 h-3.5" /> Livré + merci (WhatsApp)
                </button>
              )}
              {["a_livrer", "en_cours"].map((s) => (
                <button key={s} disabled={busy === o.id} data-testid={`delivery-${s}-${o.id}`} onClick={() => setDelivery(o, s)}
                  className={`text-xs px-3 py-2 rounded-full font-medium border ${o.delivery_status === s ? "bg-brand text-white border-brand" : "border-brand-line text-brand-sub hover:bg-brand-cream"}`}>
                  {DELIVERY_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {done.length > 0 && (
        <div>
          <h2 className="font-heading font-semibold text-brand-ink mb-3 flex items-center gap-2"><Truck className="w-5 h-5 text-brand-accent" /> Livrées récemment</h2>
          <div className="space-y-2">
            {done.slice(0, 10).map((o) => (
              <div key={o.id} className="bg-white border border-brand-line rounded-xl p-3 flex justify-between text-sm">
                <span className="text-brand-sub">{o.order_number} · {o.customer_name}</span>
                <span className="text-brand-muted">{formatDate(o.updated_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {livreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="livre-modal" onClick={() => setLivreModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-lg text-brand-ink">Confirmer la livraison</h3>
            <p className="text-sm text-brand-muted mt-1">Commande {livreModal.order_number} · {livreModal.customer_name}</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-brand-sub">Nom du livreur</label>
                <input value={livreurName} onChange={(e) => setLivreurName(e.target.value)} placeholder="Ex : Kodjo (moto)" data-testid="livreur-name-input"
                  className="w-full mt-1 border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
              </div>
              <div>
                <label className="text-sm font-medium text-brand-sub">Montant demandé par le livreur (FCFA)</label>
                <input type="number" value={livreurFeeProposed} onChange={(e) => setLivreurFeeProposed(e.target.value)} placeholder="Ex : 1500" data-testid="livreur-fee-proposed-input"
                  className="w-full mt-1 border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
                <p className="text-xs text-brand-muted mt-1">Ce que le livreur réclame, avant négociation.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-brand-sub">Montant final payé au livreur (FCFA)</label>
                <input type="number" value={livreurFee} onChange={(e) => setLivreurFee(e.target.value)} placeholder="0" data-testid="livreur-fee-input"
                  className="w-full mt-1 border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
                <p className="text-xs text-brand-muted mt-1">Montant réellement payé après négociation. Frais de livraison de la commande : {fcfa(livreModal.delivery_fee || 0)}. Laissez 0 si le livreur n'est pas payé pour celle-ci.</p>
                {Number(livreurFeeProposed) > 0 && Number(livreurFee) > 0 && Number(livreurFeeProposed) !== Number(livreurFee) && (
                  <p className="text-xs font-semibold mt-1 text-green-600" data-testid="livreur-negotiation-note">
                    💡 Économie négociée : {fcfa(Math.max(Number(livreurFeeProposed) - Number(livreurFee), 0))}
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none bg-brand-cream rounded-lg px-3 py-2.5" data-testid="livreur-paid-toggle">
                <input type="checkbox" checked={livreurPaid} onChange={(e) => setLivreurPaid(e.target.checked)} className="w-4 h-4 accent-brand" />
                <span className="text-sm font-medium text-brand-ink">J'ai déjà payé le livreur ✅</span>
              </label>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setLivreModal(null)} className="px-4 py-2.5 rounded-full text-sm font-semibold text-brand-sub hover:bg-brand-cream">Annuler</button>
              <button onClick={confirmLivree} disabled={busy === livreModal.id} data-testid="confirm-livree-button"
                className="bg-brand text-white hover:bg-brand-hover rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                {busy === livreModal.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />} Confirmer livré
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
